// Shared event domain types — single source of truth for the app (src/) and Cloud Functions (functions/).
// This module MUST stay self-contained (no imports) so it compiles cleanly under both the app's
// `bundler` resolution and functions' `nodenext` resolution. See /shared/tsconfig.json.
//
// Originally generated by Firefoo v1.5.11 on 2023-11-07; relocated here from src/types/event.ts.
//
// ⚠️ DUAL AUDIENCE: the comments in this file (down to the runtime-init-constants marker near the
// bottom) are served VERBATIM to the AI assistant as `events` schema documentation, via
// functions/scripts/embed-schemas.cjs → the getCollectionSchema tool. Write field comments for BOTH
// developers and the assistant: prefer plain-language business meaning ("prime-date pricing override")
// over restating the type. Everything below the marker (init constants) is excluded from that copy.

// --- Act rate categories (moved from constants/optionLists.ts) ---
export const actRateCategories = ['Ensemble', 'Main Band', 'Synergy'] as const;
export type ActRateCategory = (typeof actRateCategories)[number];

// --- Act / entertainment type (moved from src/types/config.ts) ---
export interface EntType {
  // AKA Act
  id: string;
  name: string;
  color: any;
  writeIn?: boolean;
  tags?: string[];
  thumbnailUrl?: string;
  driveFolderId?: string;
  actRateCategory?: ActRateCategory;
}

// --- Contract addressee method (moved from pages/.../ContractAddressMethodEnums.ts) ---
// Member order is significant — these are positional numeric enum values.
export enum AddressMethod {
  ClientNamesOnly,
  ClientCompanyAndClientName,
  ClientNamesAndPlannerName,
  ClientNamesAndPlannerCompany,
  PlannerNameOnly,
  PlannerCompanyOnly,
  PlannerCompanyAndPlannerName
}

// --- Event status + travel options (moved from constants/optionLists.ts) ---
export const eventStatuses = [
  'lead',
  'quote sent',
  'pending',
  'booked',
  'sub-event',
  'dead',
  'guarantee'
] as const;
export type EventStatus = (typeof eventStatuses)[number];

export const travelOptions = [
  '30 min from office',
  '1.5 hours from office',
  '3 hours from office',
  'Flight',
  'Yes, but unknown'
] as const;
export type TravelOption = (typeof travelOptions)[number];

export interface ContactQuickObject {
  name: string;
  id: string;
}

export interface DetailsValues {
  staticDetailsFields: StaticDetailsFields;
  flexDetailsFields: FlexDetailsField[];
  clientFriendlyTitle: string;
  clAlsoInterestedIn: string;
  dateStart: Date | null;
  links: Link[];
}

export interface AttireByTag {
  tagId: string;
  tagName: string;
  attire: string | null;
}

export interface StaticDetailsFields {
  attire?: string;
  attireByContactTag?: AttireByTag[];
  greenRoom?: string;
  greenRoomLocation?: string;
  loadInInfo?: string;
  onSiteContact?: string;
  venueContact?: string;
  plannerContact?: string;
  parkingDetails?: string;
  performanceLocations?: string;
  producerNotes?: string;
  producerToBring?: string;
  soundContact?: string;
  soundNotesInternal?: string;
  soundNotes?: string;
  vendorMealCount?: string;
  wifi?: string;
  travel?: string;
  internalClientCallOne?: string;
  internalClientCallTwo?: string;
  internalBandStands?: string;
  inputList?: string;
  backlineNotes?: string;
}

export interface ContractBreaks {
  contractTotals?: boolean;
  contractTerms?: boolean;
  contractPaymentInfo?: boolean;
  pageBreakBeforeGroups?: string[];
}

export interface InvoiceBreaks {
  invoiceTotals?: boolean;
  invoicePaymentInfo?: boolean;
  pageBreakBeforeGroups?: string[];
}

export interface QuoteBreaks {
  quoteTotals?: boolean;
  pageBreakBeforeGroups?: string[];
}

export interface Link {
  title: string;
  url: string;
  color: string;
}

// The central booking record. One doc per event/lead in the `events` collection; most other
// collections (gigs, services, deposits, expenses, histories, tasks) link back via `relEventId`.
export interface Event {
  /** Firestore document id (also stored on the doc). */
  id: string;

  // --- Identity / titles ---
  /** Internal event title (staff-facing headline). */
  eventTitle: string;
  /** Short internal nickname; when set, the app prefers it as the display label over eventTitle. */
  nickName?: string;
  /** Client-facing event title used on contracts, quotes, and the client portal. */
  clientFriendlyTitle?: string;

  // --- Status / classification ---
  /** Booking pipeline stage. Drives routing, reports, and status-change side effects. */
  status: (typeof eventStatuses)[number]; // lead, quote sent, pending, booked, sub-event, dead, guarantee
  /** The act / entertainment offering booked — aka the band or type of performance. NOT the pipeline
   *  status (Booked/Lead live on `status`). */
  eventType?: EntType;
  /** Client-supplied event category, e.g. Wedding, Corporate, etc. */
  clTypeofEvent?: string;
  /** Free-text elaboration on the event type; also feeds contract/quote "type of event" copy. */
  clTypeofEventDetailed?: string;

  // --- Timing (timezone-naive wall-clock — see useProcessTimeStamps) ---
  /** Event date. */
  dateStart: Date | null;
  /** Event start time (wall-clock; timezone-naive). */
  timeStart?: Date | null;
  /** Event end time (wall-clock; timezone-naive). */
  timeEnd?: Date | null;
  /** Timestamp stamped when the event was moved to a booked status; used in sales reports. */
  bookedDateActual?: Date | null;
  clDateBooked: Date | null; // not in use and can be removed

  // --- People: clients ---
  /** Client contacts on this event (billing, planner, addressees). See the Client interface. */
  clients: Client[];
  /** Denormalized ids of the client contacts, for querying/joins. */
  clientIds?: string[];
  /**
   * Ad-hoc Cc addresses for the contract-delivery email — people who are NOT
   * contacts on this event (the client's assistant, an outside planner). Client
   * contacts are Cc'd via `Client.contractCc` instead. Set from the Deliver
   * dialog and reused to prefill it on the next send. Additive + optional.
   */
  contractCcEmails?: string[];
  /** Same as `contractCcEmails`, for the invoice-link email. */
  invoiceCcEmails?: string[];

  // --- People: internal team (ContactQuickObject = { id, name }) ---
  /** Bandleader assigned to the event. */
  bandleader?: ContactQuickObject;
  /** Musical director assigned to the event. */
  musicalDirector?: ContactQuickObject;
  /** Sound engineer assigned to the event. */
  soundEngineer?: ContactQuickObject;
  /** Strings section leader. */
  strings?: ContactQuickObject;
  /** Horns section leader. */
  horns?: ContactQuickObject;
  /** Percussion section leader. */
  percussion?: ContactQuickObject;
  /** Pre-production lead (staff owner of prep). */
  preProdLead?: ContactQuickObject;
  /** Producer assigned to the event. */
  producer?: Producer;
  /** Salesperson who owns the deal; feeds sales reporting. */
  salesLead?: SalesLead;

  // --- Ratings (internal 1–5 staff scores) ---
  /** Internal difficulty score (1–5). Staff-only. */
  complexityRating?: number;
  /** Internal quality score (1–5). Staff-only; aggregated in the sales report. */
  qualityRating?: number;

  // --- Finance / contract ---
  /** Discount applied to the event total. */
  discount?: number;
  /** Whether the event has been marked paid. */
  paidFlag?: boolean;
  /** Internal free-text financial notes. Staff-only. */
  financialNotes?: string;
  /** Contract legal terms text. */
  terms?: string;
  /** Version marker of the currently-issued contract; cleared to re-open/reset signing. */
  contractVersion?: string;
  /** How the contract addressee line is composed (client/planner names & companies). */
  contractAddressMethod?: AddressMethod;
  /** Explicit override string for the contract "address to" line. */
  contractAddressTo?: string;
  /** Prime-date pricing override for services. "Prime" dates carry higher rates; by default a date is
   *  prime when it falls on a Saturday (isPrime(dateStart) === (dateStart.getDay() === 6)). When this
   *  field is set it overrides that derivation, forcing prime (truthy) or non-prime (falsy) pricing
   *  regardless of the weekday. */
  eventPrime?: number | boolean;

  // --- PDF section page-break controls ---
  /** @deprecated all-or-nothing jobsheet page-break toggle; replaced by pageBreakBeforeJobsheet. */
  breakPDFSections?: boolean;
  // Keys of jobsheet headings (document sections, segments, and General/Timeline subgroups) that
  // should start on a new page. Replaces the all-or-nothing breakPDFSections toggle.
  pageBreakBeforeJobsheet?: string[];
  /** Per-section page-break config for the contract PDF. */
  breakContractSections?: ContractBreaks;
  /** Per-section page-break config for the invoice PDF. */
  breakInvoiceSections?: InvoiceBreaks;
  /** Per-section page-break config for the quote PDF. */
  breakQuoteSections?: QuoteBreaks;

  // --- Details tab (logistics). Heavy; served to the assistant only via getEventData includeDetails. ---
  /** Custom "flex" detail fields — the run-of-show timeline + custom logistics fields (Details tab).
   *  Rich structure incl. client-portal responses; see FlexDetailsField. */
  flexDetailsFields?: FlexDetailsField[];
  /** Static logistics fields (attire, sound, load-in, parking, etc.). See StaticDetailsFields. */
  staticDetailsFields: StaticDetailsFields;
  /** "Internal Notes" free-text on the Details Core card (legacy name: also-interested-in). Staff-only. */
  clAlsoInterestedIn?: string;

  // --- Google Drive job folder ---
  /** Drive id of the event's job folder. */
  jobFolderId?: string;
  /** Drive id of the protected (restricted) subfolder within the job folder. */
  protectedFolderId?: string;
  /** Drive file id of the generated job file/sheet. */
  jobFileId?: string;
  /** Whether the job is closed/archived. */
  jobClosed?: boolean;

  // --- External integrations ---
  /** Google Calendar event id (mirrored calendar entry). */
  googleCalEventId?: string;
  /** Last time the Google Calendar entry was synced. */
  googleCalLastSync?: Date | null;
  /** Monday.com board id linked to this event; the app builds a board URL from it. */
  mondayBoardId?: number;
  /** Monday.com board URL (when stored directly). */
  mondayBoardUrl?: string;

  // --- Lead / referral ---
  /** Referral / lead-source tags for this event (leads report). */
  referral?: string[];
  jobLeadSource?: string; // TODO(verify): lead-source string; no live reads found in src/ or functions/.

  // --- Client portal ---
  /** Timestamp of the client's most recent client-portal answer submission. */
  lastPortalEdit?: Date;
  portalTemplate: string; // TODO(verify): portal template selector; no live reads found.

  // --- Denormalized progress ---
  /** Denormalized open-task counter, kept in sync by useTaskProgressSync; null when tracking is off. */
  taskProgress?: { done: number; total: number } | null;

  // --- Payments ---
  /**
   * Active recurring (pre-approved) managed-payment method id for this event.
   * Denormalized from `securePaymentMethods` by the managed-payment functions so
   * the client can flag pre-approved invoices without reading that Admin-only
   * collection. Cleared when the method is purged.
   */
  recurringPaymentMethodId?: string | null;

  // --- Relationships / venue ---
  /** Venue booked for the event. See the Venue interface. */
  venue?: Venue;
  relSubEventId?: string; // should be relParentEventId

  // --- Audit metadata ---
  createdBy: string;
  createdDate: Date | null;
  createdTime: string;
  modifiedBy: string;
  modifiedDate: Date | null;
  modifiedTime: string;

  // --- Misc / links ---
  /** External reference links shown on the event. */
  links?: Link[];
  /** Travel requirement for the booking (distance bucket, flight, or unknown). */
  travel: TravelOption | 1 | 0 | '' | null;

  // --- TODO(verify): legacy / unclear — no live reads found in src/ or functions/. Some (attire,
  //     loadInInfo) look like stale duplicates of the same-named StaticDetailsFields keys. ---
  attire?: string;
  loadInInfo?: string;
  avTech?: string; // sound-staff detail copied onto gig dashboard payloads; also present on gig docs.
  careOf?: string;
  otmContact?: number;
  techConfirmed?: boolean;
  jobCheatSheet?: string;
  wardrobeNeeded?: boolean;
}

export interface TimelineItem {
  label?: string;
  time: Date | null;
}

export interface ClientResponse {
  /** The client's answer for the field — text/boolean/etc. Same meaning for every field type. */
  value: PortalValue;
  /**
   * Details v2: a client's proposed timeline times for a `timeline:true` field. Additive + optional,
   * so legacy responses (which never carry them) are unaffected and never error. The canonical
   * `field.start`/`field.end` stay staff-owned until accepted; these hold the client's proposal.
   * `start`/`end` keys are whitelisted no-offset in useProcessTimeStamps wherever they appear.
   */
  start?: Date | null;
  end?: Date | null;
  timestamp?: Date | null;
}

export type PortalValue = string | boolean | TimelineItem[] | { time: Date | null } | null;

export interface FlexDetailsField {
  id?: string;
  title: string;
  value: PortalValue;
  timestamp: Date | null;
  desc: string;
  onDashboard: boolean;
  onJobsheet: boolean;
  onPortal: boolean;
  includeTime: boolean;
  showInfoText: boolean;
  clientResponseHistory: ClientResponse[];
  clientResponse: ClientResponse;
  type: 'standard' | 'richText' | 'boolean' | 'image' | 'timeline' | 'section';
  finalized?: boolean;
  finalizedBy?: string;
  finalizedAt?: Date | null;
  hideFromFlex?: boolean;
  onTasks?: boolean;
  /**
   * Second dashboard destination, narrowed to A/V & Tech contractors. Independent of and OR'd with
   * `onDashboard`: a field shows on the talent dashboard when `onDashboard` is set (everyone), or
   * when this is set and the viewer qualifies as A/V & Tech — meaning the role they are booked in
   * on that gig falls under the `A/V & Tech` tag, or their contact record has the
   * `techInfoDashboard` override. This is a DISPLAY FILTER, not confidentiality: the talent app
   * still receives every field of the event, so do not put restricted material behind it.
   * Additive + optional; absent reads as false. Talent dashboard only — jobsheet/portal/tasks
   * ignore it.
   */
  onDashboardAvTech?: boolean;
  // --- Details v2 (Segments tab) — additive, optional; ignored by legacy reads/writes ---
  /** Marks this field as a timeline (grid) row rather than a header/general field. */
  timeline?: boolean;
  /** Timeline start time (picker wall-clock — see useProcessTimeStamps no-offset whitelist). */
  start?: Date | null;
  /** Timeline end time (picker wall-clock). */
  end?: Date | null;
  /** Link to the owning Segment doc (`segments` collection). The new grouping relationship. */
  segmentId?: string;
  /**
   * Explicit display order within a segment's general grid (drag-reorderable). Additive: unset
   * fields sort last (append order), so legacy data renders exactly as before until first reordered.
   */
  order?: number;
  /**
   * Tag IDs from the `All Tags › Talent` branch of the tag tree (`config/tagSets`). Multiple
   * per row; resolved to names for display via `getNameFromId(id, contactTagObject)`. Additive +
   * backward compatible — legacy reads/writes ignore it.
   */
  tags?: string[];
  /**
   * Image gallery for `type:'image'` fields — the full set of image URLs in display order.
   * Additive + backward compatible: `value` always holds the PRIMARY image (`images[0]`), so
   * legacy single-image consumers (dashboard, jobsheet PDF, GetPreview) keep working unchanged.
   * A legacy image field with only `value` is read as `[value]`.
   */
  images?: string[];
  /**
   * Custom columns, self-contained on the item (no separate registry). Each entry is
   * `{ id, name, value }`: `id` a stable column id, `name` the editable header, `value` sanitized
   * rich-text HTML (same shape as a richText `value`). A segment's column set is derived from the
   * union (by `id`) of these entries across its fields. Additive + backward compatible: legacy
   * fields have none; a missing entry reads as ''. External-facing: rendered (grouped under their
   * field) on the talent dashboard and jobsheet PDF, gated by the field's `onDashboard`/`onJobsheet`.
   * A cell can also be delegated to the client portal per-cell (`onPortal`) — see `CustomColumnValue`.
   * TEXT FIELDS ONLY: custom columns are rich text, so they are neither edited nor rendered on
   * `boolean`, `image` or legacy `section` items — see `supportsCustomCols`.
   */
  customCols?: CustomColumnValue[];
}

/**
 * Whether a flex item carries custom columns (`FlexDetailsField.customCols`). Custom cells are always
 * rich text, so they belong to text items only — a `boolean`, `image` or legacy `section` item never
 * shows or edits them, in ANY consumer (grid, detail panel, summary, dashboard, jobsheet, portal).
 * Stale entries left on such an item by earlier data are simply ignored, not migrated.
 *
 * Absent/legacy `type` reads as text, matching the detail panel's type selector, which folds anything
 * that isn't `boolean`/`image` into `richText`.
 */
export const supportsCustomCols = (type?: FlexDetailsField['type']): boolean =>
  type !== 'boolean' && type !== 'image' && type !== 'section';

/** One custom-column cell on a flex item — see `FlexDetailsField.customCols`. */
export interface CustomColumnValue {
  /** Stable column id; also the DataGrid column `field`. Matches the column across rows. */
  id: string;
  /** Editable column header. Renamed in place; values stay put because they key on `id`. */
  name: string;
  /** Sanitized rich-text HTML (same shape as a richText `value`). */
  value: string;
  /**
   * Per-cell portal delegation — the custom-column analog of `FlexDetailsField.onPortal`, but at cell
   * grain. When true, the client sees this cell as an indented sub-question and answers into
   * `clientResponse`. Additive + optional: legacy/undelegated cells omit it (read as not-on-portal).
   * Custom cells are always rich-text, so `clientResponse` only ever uses `value`/`timestamp` (no
   * timeline start/end).
   */
  onPortal?: boolean;
  /** Staff cell edit time — the cell analog of `FlexDetailsField.timestamp`; drives the attention
   *  comparison against `clientResponse.timestamp`. Stamped when staff edits or accepts the value. */
  timestamp?: Date | null;
  /** The client's answer to this delegated cell (value + timestamp; times unused). */
  clientResponse?: ClientResponse;
  /** Prior client answers, newest pushed on change (mirrors `FlexDetailsField.clientResponseHistory`). */
  clientResponseHistory?: ClientResponse[];
}

/**
 * Details v2 container (the `segments` collection — one doc per segment).
 * Holds segment-level metadata ONLY — never the flex fields themselves, which stay in
 * `event.flexDetailsFields` and link back via `FlexDetailsField.segmentId`.
 */
export interface Segment {
  id: string;
  relEventId: string;
  title: string;
  /** Authored segment color (raw hex from the palette picker). Adjusted display-only per theme mode
   *  (lightened on dark surfaces, darkened on light) — never persisted adjusted. Cleared to null. */
  color?: string | null;
  /** Authored segment time window (wall-clock, timezone-naive). When present, overrides the derived
   *  earliest-start / latest-end window from the segment's timeline items. Written ONLY on explicit
   *  user confirm (never a derived auto-persist) — see SegmentHeaderBar. Cleared to null to fall back
   *  to the derived window. */
  timeStart?: Date | null;
  timeEnd?: Date | null;
  finalized?: boolean;
  finalizedBy?: string;
  finalizedAt?: Date | null;
  onTasks?: boolean;
  /** Talent roster: when true, this segment gets a per-gig assignment check column and appears in
   *  the bulk assign/remove segment menus. Opt-in — unset/false means not assignable. */
  rosterAssignable?: boolean;
  order: number;
  modified?: Date | null;
  modifiedBy?: string;
}

export interface Client {
  city?: string;
  email1?: string;
  id: string;
  name: string;
  phone?: string;
  planner?: boolean;
  state?: string;
  street?: string;
  zip?: number | string;
  /** @deprecated Use includeOnContract instead */
  primary?: boolean;
  /** Whether this client's name should appear on the contract addressee line */
  includeOnContract?: boolean;
  /** @deprecated Use billingContact instead */
  onContract?: boolean;
  /** Whether this client's company and address should be used for billing/mailing */
  billingContact?: boolean;
  /** Contract-only: include this client on the Cc line of the contract email. */
  contractCc?: boolean;
  /** Invoice-only: include this client on the Cc line of the invoice-link email. */
  invoiceCc?: boolean;
  portal?: boolean;
  description?: string;
  [key: string]: any;
}

export interface Producer {
  id: string;
  name: string;
  writeIn?: boolean;
}

export interface SalesLead {
  id: string;
  name: string;
}

export interface Venue {
  id: string;
  name: string;
  description?: string;
  writeInAddress?: any;
  writeIn?: boolean;
  [key: string]: any;
}

// --- runtime init constants (below this line: excluded from assistant schema docs) ---

export const clientInit: Client = {
  id: '',
  name: '',
  includeOnContract: false,
  billingContact: false
};

export const venueInit: Venue = {
  id: '',
  name: '',
  desc: '',
  writeInAddress: {
    add1City: '',
    add1State: '',
    add1Street: '',
    add1StreetB: '',
    add1Zip: '',
    desc: ''
  }
};

export const staticDetailsFieldsInit: StaticDetailsFields = {
  attire: '',
  attireByContactTag: [],
  greenRoomLocation: '',
  loadInInfo: '',
  onSiteContact: '',
  venueContact: '',
  plannerContact: '',
  producerToBring: '',
  parkingDetails: '',
  performanceLocations: '',
  vendorMealCount: '',
  producerNotes: '',
  wifi: '',
  travel: '',
  internalClientCallOne: '',
  internalClientCallTwo: '',
  internalBandStands: '',
  soundContact: '',
  soundNotesInternal: '',
  soundNotes: '',
  inputList: '',
  backlineNotes: ''
};

export const eventInit: Event = {
  attire: '',
  avTech: '',
  bookedDateActual: null,
  bandleader: {
    id: '',
    name: ''
  },
  musicalDirector: {
    id: '',
    name: ''
  },
  soundEngineer: {
    id: '',
    name: ''
  },
  strings: {
    id: '',
    name: ''
  },
  horns: {
    id: '',
    name: ''
  },
  percussion: {
    id: '',
    name: ''
  },
  careOf: '',
  clAlsoInterestedIn: '',
  clDateBooked: null,
  clTypeofEvent: '',
  clTypeofEventDetailed: '',
  clients: [],
  complexityRating: 0,
  qualityRating: 0,
  createdBy: '',
  createdDate: null,
  createdTime: '',
  contractAddressMethod: AddressMethod.ClientNamesOnly,
  contractAddressTo: '',
  dateStart: null,
  discount: 0,
  eventPrime: 0,
  eventTitle: '',
  eventType: {
    id: '',
    name: '',
    color: ''
  },
  financialNotes: '',
  flexDetailsFields: [
    {
      title: '',
      value: '',
      timestamp: null,
      desc: '',
      onDashboard: true,
      onJobsheet: true,
      onPortal: false,
      includeTime: false,
      showInfoText: false,
      type: 'standard',
      clientResponseHistory: [],
      clientResponse: { value: '', timestamp: null }
    }
  ],
  googleCalEventId: '',
  id: '',
  jobCheatSheet: '',
  jobClosed: false,
  jobFileId: '',
  jobFolderId: '',
  jobLeadSource: '',
  staticDetailsFields: staticDetailsFieldsInit,
  loadInInfo: '',
  links: [],
  modifiedBy: '',
  modifiedDate: null,
  modifiedTime: '',
  mondayBoardId: 0,
  mondayBoardUrl: '',
  nickName: '',
  otmContact: 0,
  paidFlag: false,
  portalTemplate: '',
  preProdLead: {
    id: '',
    name: ''
  },
  producer: {
    id: '',
    name: ''
  },
  referral: [],
  salesLead: {
    id: '',
    name: ''
  },
  status: 'lead',
  taskProgress: null,
  timeEnd: null,
  timeStart: null,
  terms: '',
  techConfirmed: false,
  travel: 0,
  venue: venueInit,
  wardrobeNeeded: false
};
